import React, { useEffect, useState } from "react";
import { StatusCode } from "api/protocols";
import { ButtonNew } from "components/buttonNew";
import Card from "components/card/Card";
import DatePickerInput from "components/datePickerInput/DatePickerInput";
import Page from "components/page/Page";
import { useTranslation } from "react-i18next";
import { GetShifts } from "api/rpc/2024-04/facilityAdmin/client/shifts";
import ButtonGroup from "components/button/ButtonGroup";
import useModal from "hooks/modals/useModal";
import Sheet from "components/sheet/Sheet";
import Input from "components/form/input/Input";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import { getOpenTablesForUser } from "helpers/openTables";
import OpenTablesWarningPopup from "components/openTablesWarning/OpenTablesWarningPopup";
import { ITable } from "api/rpc/2024-04/facilityAdmin/tables/tables";
import useDebounce from "hooks/events/useDebounce";
import axios, { CancelToken } from "axios";
import { GetFacilityAdmin, IFacilityAdmin } from "api/rpc/2024-04/facilityAdmin/client/admin/admin";
import SelectNew from "components/select/SelectNew";
import { showError } from "redux/actions/ui";

interface IFilters {
  search: string;
  staff: IFacilityAdmin[];
  selectedStaff: IFacilityAdmin[];
}

export default function Shifts() {
  const { t } = useTranslation();
  const { authStore } = useAppSelector(store => store);
  const [date, setDate] = useState<Date | null>(null);
  const dispatch = useAppDispatch();
  const [kitchenPercentage, setKitchenPercentage] = useState<number>(0);
  const [filters, setFilters] = useState<IFilters>({
    search: "",
    staff: [],
    selectedStaff: [],
  });

  const [barPercentage, setBarPercentage] = useState<number>(0);
  const [supportPercentage, setSupportPercentage] = useState<number>(0);
  const [openTablesModal, setOpenTablesModal] = useState<{ isOpen: boolean; tables: ITable[]; preview: boolean }>({
    isOpen: false,
    tables: [],
    preview: false,
  });

  const debouncedSearch = useDebounce(filters.search, 500);

  const {
    state: previewModal,
    closeModal: closePreviewModal,
    updateModal: updatePreviewModal,
  } = useModal({ html: "" });

  useEffect(() => {
    const source = axios.CancelToken.source();
    void loadAdminAccounts(source.token);
    return () => source.cancel();
  }, [debouncedSearch]);

  async function loadAdminAccounts(token?: CancelToken) {
    const res = await GetFacilityAdmin(
      {
        search: filters.search,
      },
      token ? false : true,
      token,
    );

    if (res.status !== StatusCode.OK) {
      dispatch(showError("Error searching admins"));
      return;
    }

    setFilters(prev => ({ ...prev, staff: res.data }));
  }

  async function handleShifts(preview: boolean) {
    if (!date) {
      return;
    }

    const openTables = await getOpenTablesForUser(authStore.user?.id);

    if (openTables.length > 0) {
      setOpenTablesModal({ isOpen: true, tables: openTables, preview });
      return;
    }

    await fetchShiftSummary(preview);
  }

  async function fetchShiftSummary(preview: boolean) {
    const staffIDs = filters.selectedStaff.map(staff => {
      return staff.id;
    });

    const ShiftsRes = await GetShifts(
      {
        date: date.toDateString(),
        kitchen_percent: kitchenPercentage,
        bar_percent: barPercentage,
        support_percent: supportPercentage,
        staff_ids: staffIDs,
        suppress_print: staffIDs?.length > 1 ? true : false,
      },
      true,
    );

    console.log("res", ShiftsRes.data);

    if (ShiftsRes.status !== StatusCode.OK) {
      console.log(ShiftsRes.message);
      return;
    }

    if (preview === true) {
      const view = ShiftsRes.data.join(
        '<div style="border-top: 2px solid #e7e7e7; margin: 24px 0; width: 100%; clear: both;"></div>',
      );

      updatePreviewModal({ isOpen: true, html: view });
    } else {
      const printWindow = window.open();

      if (!printWindow) {
        return;
      }
      const doc = ShiftsRes.data.join("");
      printWindow.document.write(doc as string);

      printWindow.print();
      printWindow.close();
    }
  }

  function handleContinueWithOpenTables() {
    const preview = openTablesModal.preview;
    setOpenTablesModal({ isOpen: false, tables: [], preview: false });
    void fetchShiftSummary(preview);
  }

  function printShiftSummary() {
    const printWindow = window.open();

    const html = previewModal.html.replace(
      /<div style="border-top: 2px solid #e7e7e7; margin: 24px 0; width: 100%; clear: both;"><\/div>/g,
      "",
    );

    printWindow.document.write(html);
    printWindow.print();
    printWindow.close();
  }

  function handleSelectStaff(newSelectedStaff: IFacilityAdmin) {
    let updated;

    if (filters.selectedStaff.some(staff => staff.id === newSelectedStaff.id)) {
      updated = filters?.selectedStaff.filter(staff => staff.id !== newSelectedStaff.id);
    } else {
      updated = [...filters.selectedStaff, newSelectedStaff];
    }

    if (filters.search !== "") {
      setFilters(prev => ({ ...prev, search: "" }));
    }

    setFilters(prev => ({ ...prev, selectedStaff: updated }));
  }

  const getStaffPlaceholders = () => {
    return filters.selectedStaff.map(staff => staff.full_name).join(", ");
  };

  return (
    <Page title={t("Shifts")} subtitle={t("Shifts for employees")} narrow>
      <Card>
        <Card.Section>
          <p className="mt-2">{t("Pick a Date")}</p>
          <DatePickerInput
            months={2}
            position={"left"}
            startingDate={date}
            setStartingDate={returnDate => setDate(returnDate)}
          />
          {authStore?.user?.permissions?.admin_view_shift_summary ? (
            <div className="mt-3">
              <SelectNew
                label={t("Admin Search")}
                id="admin_search"
                options={
                  filters?.staff.map(staff => ({
                    id: staff.id,
                    label: staff?.full_name,
                    extraValue: staff,
                  })) ?? []
                }
                placeholder={
                  filters.selectedStaff?.length > 0
                    ? getStaffPlaceholders()
                    : t("No Staff Selected - Leave empty to print your own summary")
                }
                multiValue={filters.selectedStaff?.map(staff => {
                  return staff.id;
                })}
                onChange={(_, staff: IFacilityAdmin) => handleSelectStaff(staff)}
                searchable
                onSearchCallback={search => setFilters(prev => ({ ...prev, search: search }))}
                tooltip={t("Leave empty to print your own shift summary")}
              />
            </div>
          ) : null}

          <div style={{ display: "flex", gap: "16px", marginTop: "16px" }}>
            <Input
              id="kitchen_percent"
              label={t("Kitchen Percentage (%)")}
              type="number"
              value={kitchenPercentage.toString()}
              onChange={e => setKitchenPercentage(Number(e.target.value))}
            />
            <Input
              id="bar_percent"
              label={t("Bar Percentage (%)")}
              type="number"
              value={barPercentage.toString()}
              onChange={e => setBarPercentage(Number(e.target.value))}
            />
            <Input
              id="support_percent"
              label={t("Support Percentage (%)")}
              type="number"
              value={supportPercentage.toString()}
              onChange={e => setSupportPercentage(Number(e.target.value))}
            />
          </div>

          <ButtonGroup>
            <ButtonNew className="mt-4" type="primary" onClick={() => handleShifts(false)}>
              {t("Print Shift Summary")}
            </ButtonNew>

            <ButtonNew className="mt-4" type="secondary" onClick={() => handleShifts(true)}>
              {t("Preview Shift Summary")}
            </ButtonNew>
          </ButtonGroup>
        </Card.Section>
      </Card>

      <Sheet
        title={t("Preview Shift Summary")}
        open={previewModal.isOpen}
        size="small"
        closable
        onCancel={closePreviewModal}
        onOk={printShiftSummary}
        okText={t("Print")}
      >
        <div dangerouslySetInnerHTML={{ __html: previewModal.html }}></div>
      </Sheet>

      <OpenTablesWarningPopup
        open={openTablesModal.isOpen}
        tables={openTablesModal.tables}
        onCancel={() => setOpenTablesModal({ isOpen: false, tables: [], preview: false })}
        onContinue={handleContinueWithOpenTables}
        continueText={openTablesModal.preview ? t("Preview Anyway") : t("Print Anyway")}
      />
    </Page>
  );
}
